import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  NG_VALIDATORS,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { TranslationService } from 'src/app/service/api/translation.service';
import { TranslationRequest } from 'src/app/shared/models/TranslationRequest';
import { ErrorMessageLogicService } from 'src/app/shared/services/error-message-logic.service';
import { FormValidatorsService } from 'src/app/shared/services/form-validators.service';

export interface PasswordFormValues {
  password: string;
  confirmPassword: string;
}

@Component({
  selector: 'app-password',
  template: `
    <ng-container [formGroup]="passwordGroup" role="none">
      <ul role="none" class="tw-flex tw-flex-wrap">
        <li
          role="none"
          class="tw-w-full tw-flex tw-flex-col md:tw-flex-row tw-items-center app-mt2"
        >
          <label for="newPassword" class="tw-w-full md:tw-w-[50%]"
            >{{ newPasswordText | translation : translationRequest }}: *</label
          >
          <mat-form-field
            [@.disabled]="animationsDisabled"
            class="tw-w-full md:tw-w-[50%] app-mb2"
          >
            <input
              matInput
              required
              autocomplete="new-password"
              id="newPassword"
              [type]="showPassword ? 'text' : 'password'"
              formControlName="newPassword"
              [attr.aria-label]="newPasswordText | translation : translationRequest | sentenceCase"
              [ngClass]="{ 'is-invalid': showErrorField('newPassword') }"
            />
            <mat-error *ngIf="showErrorField('newPassword')" class="neg-mt">
              <mat-error
                class="error-message"
                *ngIf="newPassword.hasError('required')"
              >
                <span> {{'Error :' | translation : translationRequest | sentenceCase}} {{ newPasswordText | translation : translationRequest | sentenceCase }} {{'is required.' | translation : translationRequest | sentenceCase}}</span>
              </mat-error>
              <mat-error
                class="error-message"
                *ngIf="
                  newPassword.hasError('minlength') ||
                  newPassword.hasError('pattern')
                "
              >
                <span>
                 {{"Error : The password doesn't meet the requirements." | translation : translationRequest | sentenceCase}}</span
                >
              </mat-error>
              <mat-error
                class="error-message"
                *ngIf="newPassword.hasError('maxlength')"
              >
                <span>
                  {{"Error : The maximum length for this field is" | translation : translationRequest | sentenceCase}}
                  {{ newPassword.errors['maxlength'].requiredLength }}
                  {{"characters." | translation : translationRequest | sentenceCase}}</span
                >
              </mat-error>
            </mat-error>
            <mat-icon
              *ngIf="showEyeIcon"
              matSuffix
              tabindex="0"
              class="tw-cursor-pointer"
              (click)="showPasswordIcon()"
              [attr.aria-label]="
                showPassword ? ('Hide password' | translation : translationRequest) : ('Show password' | translation : translationRequest)
              "
            >
              {{ showPassword ? 'visibility_off' : 'visibility' }}
            </mat-icon>
          </mat-form-field>
        </li>

        <li
          role="none"
          class="tw-w-full tw-flex tw-flex-col md:tw-flex-row tw-items-center app-mt2"
        >
          <label for="confirmPassword" class="tw-w-full md:tw-w-[50%]"
            >{{ retypePasswordText | translation : translationRequest }}: *</label
          >
          <mat-form-field
            [@.disabled]="animationsDisabled"
            class="tw-w-full md:tw-w-[50%] app-mb2"
          >
            <input
              matInput
              required
              autocomplete="new-password"
              id="confirmPassword"
              [type]="showPassword ? 'text' : 'password'"
              formControlName="confirmPassword"
              [attr.aria-label]="retypePasswordText | translation : translationRequest | sentenceCase"
              [ngClass]="{ 'is-invalid': showErrorField('confirmPassword') }"
            />
            <mat-error class="neg-mt" *ngIf="showErrorField('confirmPassword')">
              <mat-error
                class="error-message"
                *ngIf="confirmPassword.hasError('required')"
              >
                <span>{{"Error :" | translation : translationRequest | sentenceCase}} {{ retypePasswordText | translation : translationRequest | sentenceCase }} {{"is required." | translation : translationRequest | sentenceCase}}</span>
              </mat-error>
            </mat-error>
            <mat-icon
              tabindex="0"
              *ngIf="showEyeIcon"
              matSuffix
              class="tw-cursor-pointer"
              (click)="showPasswordIcon()"
              [attr.aria-label]="
                showPassword ? ('Hide password' | translation : translationRequest) : ('Show password' | translation : translationRequest)
              "
            >
              {{ showPassword ? 'visibility_off' : 'visibility' }}
            </mat-icon>
          </mat-form-field>
        </li>
        <li
          role="none"
          class="tw-w-full tw-flex tw-flex-col md:tw-flex-row tw-justify-end "
        >
          <span *ngIf="passwordGroup.errors?.['match']" class="error-message">
            {{"Error : The passwords do not match." | translation : translationRequest | sentenceCase}}
          </span>
        </li>
      </ul>
    </ng-container>
  `,
  styles: [
    `
      ::ng-deep .mat-form-field-suffix {
        display: flex;
        align-items: center;
      }

      ::ng-deep .password-toggle-btn {
        background-color: var(--sed-sedgwick-green-color) !important;
        border-radius: 0 4px 4px 0;
        height: 100%;
        margin: 0;
        position: absolute;
        right: 0;
        top: 0;
      }

      ::ng-deep .password-toggle-btn .mat-icon {
        color: var(--sed-warm-white-color);
        font-size: 20px;
        height: 20px;
        width: 20px;
        line-height: 20px;
      }

      ::ng-deep .password-toggle-btn:hover .mat-icon {
        color: var(--sed-engaged-black-color) !important;
      }

      .neg-mt {
        margin-top: -1.5rem;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PasswordComponent),
      multi: true,
    },
  ],
})
export class PasswordComponent implements ControlValueAccessor, OnDestroy,OnInit {
  @Input() parentForm: UntypedFormGroup;
  @Input() parentFormGroupName: string;
  @Input() showPassword: boolean;
  @Input() newPasswordText: string = 'Password';
  @Input() retypePasswordText: string = 'Retype password';
  @Input() showEyeIcon: boolean = false;

  subscriptions: Subscription[] = [];
  passwordGroup: UntypedFormGroup;
  animationsDisabled: true;
  hidePwd: true;

  get value(): PasswordFormValues {
    return this.passwordGroup.value;
  }

  set value(value: PasswordFormValues) {
    if (this.passwordGroup.value != value) {
      this.passwordGroup.setValue(value);
    }
    this.onChange(value);
    this.onTouched();
  }

  get newPassword() {
    return this.passwordGroup.get('newPassword');
  }

  get confirmPassword() {
    return this.passwordGroup.get('confirmPassword');
  }
  translationRequest: TranslationRequest = new TranslationRequest(
    {
      language: sessionStorage.getItem('languagepreference'), // Default to English, you can make this dynamic
      key: null,
      contNum: 0,
      tableName: null,
      primaryKey: null,
      primaryValue: null,
      lob: null,
      group: 'LoginApp',
      subGroup: 'LoginApp'
    }
  );
  constructor(
    private fb: UntypedFormBuilder,
    private formValidator: FormValidatorsService,
    private errorService: ErrorMessageLogicService,
    public translationService: TranslationService,
    //private translationRequest: TranslationRequest
  ) {
    this.passwordGroup = this.buildPasswordGroup();
    //this.loadTranslations();
    this.subscriptions.push(
      this.passwordGroup.valueChanges
        .pipe(
          distinctUntilChanged(
            (a, b) => JSON.stringify(a) === JSON.stringify(b)
          )
        )
        .subscribe((value) => {
          this.onChange(value);
          this.onTouched();
          this.passwordGroup.updateValueAndValidity();
          this.parentForm
            .get(this.parentFormGroupName)
            .setValue(this.passwordGroup);
          this.parentForm.updateValueAndValidity();
        })
    );
  }
  ngOnInit(): void {
  }

  buildPasswordGroup(): UntypedFormGroup {
    return this.fb.group(
      {
        newPassword: [
          '',
          {
            validators: [
              Validators.minLength(8),
              Validators.maxLength(16),
              Validators.required,
              Validators.pattern(
                '^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[@#$%^&+=!])[A-Za-z\\d@#$%^&+=!]{8,}$'
              ),
            ],
            updateOn: 'blur',
          },
        ],
        confirmPassword: [
          '',
          {
            validators: [
              Validators.minLength(8),
              Validators.maxLength(16),
              Validators.required,
              //Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^+=@$!%*?&])[A-Za-z\d\^+=@$!%*?&].{8,}'),
              this.formValidator.matchingFields(
                'newPassword',
                'confirmPassword'
              ),
            ],
            //updateOn: "blur",
          },
        ],
        updateOn: 'submit',
      },
      {
        validators: this.formValidator.matchingFields(
          'newPassword',
          'confirmPassword'
        ),
      }
    );
  }

  showPasswordIcon(): void {
    this.showPassword = !this.showPassword;
  }

  showErrorField(formControl: string): boolean {
    return this.errorService.showErrorMessage(this.passwordGroup, formControl);
  }
 

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  registerOnChange(fn) {
    this.onChange = fn;
  }

  writeValue(value) {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.passwordGroup.reset;
    }
  }

  // communicate the inner form validation to the parent form
  validate(_: UntypedFormControl) {
    return this.passwordGroup.valid ? null : { passwords: { valid: false } };
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }
}
